/**
 * WISHLIST
 *
 * - Logged-in customers: items are rendered from the customer metafield wishlist.items and every change is sent to
 *   the app proxy (/apps/wishlist), which writes the metafield.
 * - Guests: items live in localStorage and are merged into the metafield the next time the customer is logged in.
 *
 * Every change dispatches "wishlist:change" on the document with the current items in event.detail.items.
 */
(() => {
  const STORAGE_KEY = 'nexus-wishlist';
  const configElement = document.getElementById('wishlist-config');

  if (!configElement) {
    return;
  }

  const config = JSON.parse(configElement.textContent);

  const readLocal = () => {
    try {
      const items = JSON.parse(localStorage.getItem(STORAGE_KEY));
      return Array.isArray(items) ? items.filter((item) => item && item.id) : [];
    } catch (error) {
      return [];
    }
  };

  const writeLocal = (items) => {
    try {
      if (items.length === 0) {
        localStorage.removeItem(STORAGE_KEY);
      } else {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
      }
    } catch (error) {
      // Storage can be unavailable (private mode, blocked site data); the wishlist then only lasts for this page.
    }
  };

  class WishlistStore {
    constructor() {
      this.items = config.loggedIn ? config.items : readLocal();
      this.queue = Promise.resolve();

      if (config.loggedIn) {
        this._mergeGuestItems();
      }
    }

    get limit() {
      return config.limit;
    }

    has(productId) {
      return this.items.some((item) => item.id === productId);
    }

    toggle(item) {
      return this.has(item.id) ? this.remove(item.id) : this.add(item);
    }

    add(item) {
      if (this.has(item.id)) {
        return Promise.resolve(true);
      }

      if (this.items.length >= this.limit) {
        this._notify(config.strings.limitReached);
        return Promise.resolve(false);
      }

      const previousItems = this.items;
      this._setItems([item, ...this.items]);
      return this._persist('add', [item.id], previousItems);
    }

    remove(productId) {
      if (!this.has(productId)) {
        return Promise.resolve(true);
      }

      const previousItems = this.items;
      this._setItems(this.items.filter((item) => item.id !== productId));
      return this._persist('remove', [productId], previousItems);
    }

    _setItems(items) {
      this.items = items;
      document.dispatchEvent(new CustomEvent('wishlist:change', { detail: { items: this.items } }));
    }

    _notify(message) {
      document.dispatchEvent(new CustomEvent('wishlist:notice', { detail: { message } }));
    }

    _persist(action, productIds, previousItems) {
      if (!config.loggedIn) {
        writeLocal(this.items);
        return Promise.resolve(true);
      }

      // Requests are serialized so the metafield always ends up in the same order as the clicks
      this.queue = this.queue.then(async () => {
        try {
          const serverIds = await this._request(action, productIds);
          this._reconcile(serverIds);
          return true;
        } catch (error) {
          this._setItems(previousItems);
          this._notify(error.message === 'limit' ? config.strings.limitReached : config.strings.saveError);
          return false;
        }
      });

      return this.queue;
    }

    async _request(action, productIds) {
      const response = await fetch(config.proxyUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ action, productIds })
      });

      const json = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(json.error || 'request_failed');
      }

      return json.productIds || [];
    }

    // The server is the source of truth: drop anything it did not keep (deleted products, limit...)
    _reconcile(serverIds) {
      const kept = new Set(serverIds.map(String));
      const items = this.items.filter((item) => kept.has(String(item.id)));

      if (items.length !== this.items.length) {
        this._setItems(items);
      }
    }

    async _mergeGuestItems() {
      const guestItems = readLocal().filter((item) => !this.has(item.id));

      if (guestItems.length === 0) {
        writeLocal([]);
        return;
      }

      const previousItems = this.items;
      const room = Math.max(this.limit - this.items.length, 0);
      this._setItems([...guestItems.slice(0, room), ...this.items]);

      if (await this._persist('merge', guestItems.slice(0, room).map((item) => item.id), previousItems)) {
        writeLocal([]);
      }
    }
  }

  const store = window.NexusWishlist = new WishlistStore();

  /**
   * Heart toggle rendered on product cards and the product page
   */
  class WishlistButton extends HTMLElement {
    connectedCallback() {
      this.productId = parseInt(this.getAttribute('data-product-id'), 10);
      this.toggleButton = this.querySelector('button');
      this._onChange = this._render.bind(this);
      this._onClick = this._onClick.bind(this);

      this.toggleButton.addEventListener('click', this._onClick);
      document.addEventListener('wishlist:change', this._onChange);
      this._render();
    }

    disconnectedCallback() {
      this.toggleButton.removeEventListener('click', this._onClick);
      document.removeEventListener('wishlist:change', this._onChange);
    }

    get item() {
      return JSON.parse(this.getAttribute('data-item'));
    }

    _onClick(event) {
      event.preventDefault();
      event.stopPropagation();
      store.toggle(this.item);
    }

    _render() {
      const isActive = store.has(this.productId);
      this.toggleButton.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      this.toggleButton.setAttribute('aria-label', this.toggleButton.getAttribute(isActive ? 'data-label-remove' : 'data-label-add'));
      this.classList.toggle('is-active', isActive);
    }
  }

  /**
   * Floating launcher + panel
   */
  class WishlistDrawer extends HTMLElement {
    connectedCallback() {
      this.launcher = this.querySelector('.wishlist__launcher');
      this.panel = this.querySelector('.wishlist__panel');
      this.listElement = this.querySelector('[data-list]');
      this.emptyElement = this.querySelector('[data-empty]');
      this.countElement = this.querySelector('[data-count]');
      this.countLabelElement = this.querySelector('[data-count-label]');
      this.noticeElement = this.querySelector('[data-notice]');

      this.addEventListener('click', this._onClick.bind(this));
      this.addEventListener('keydown', (event) => event.key === 'Escape' && this.open && this._setOpen(false, true));
      // composedPath is used because removing an item re-renders the list, detaching the clicked button
      document.addEventListener('click', (event) => this.open && !event.composedPath().includes(this) && this._setOpen(false));
      document.addEventListener('wishlist:change', () => this._render());
      document.addEventListener('wishlist:notice', (event) => this._showNotice(event.detail.message));

      this._render();
    }

    get open() {
      return !this.panel.hidden;
    }

    _setOpen(open, returnFocus = false) {
      this.panel.hidden = !open;
      this.launcher.setAttribute('aria-expanded', open ? 'true' : 'false');
      this.classList.toggle('is-open', open);

      if (open) {
        this.panel.querySelector('[data-action="close"]').focus();
      } else if (returnFocus) {
        this.launcher.focus();
      }
    }

    _onClick(event) {
      const target = event.target.closest('[data-action]');

      if (!target) {
        return;
      }

      switch (target.getAttribute('data-action')) {
        case 'toggle':
          this._setOpen(!this.open);
          break;

        case 'close':
          this._setOpen(false, true);
          break;

        case 'remove':
          store.remove(parseInt(target.getAttribute('data-product-id'), 10));
          break;

        case 'add-to-cart':
          this._addToCart(target);
          break;
      }
    }

    async _addToCart(button) {
      const item = store.items.find((entry) => entry.id === parseInt(button.getAttribute('data-product-id'), 10));

      if (!item) {
        return;
      }

      if (!item.singleVariant) {
        return this._openQuickBuy(item);
      }

      button.setAttribute('disabled', '');
      button.setAttribute('aria-busy', 'true');

      try {
        const response = await fetch(`${window.themeVariables.routes.cartAddUrl}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ items: [{ id: item.variantId, quantity: 1 }], sections: 'mini-cart' })
        });

        const responseJson = await response.json();

        if (!response.ok) {
          throw new Error(responseJson.description || config.strings.cartError);
        }

        if (window.themeVariables.settings.cartType === 'page') {
          window.location.href = window.themeVariables.routes.cartUrl;
          return;
        }

        const cart = await (await fetch(`${window.themeVariables.routes.cartUrl}.js`)).json();
        cart.sections = responseJson.sections;

        this._setOpen(false);
        document.documentElement.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: { cart } }));
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
          bubbles: true,
          detail: { cart, openMiniCart: window.themeVariables.settings.cartType === 'drawer' }
        }));

        if (window.themeVariables.settings.cartType !== 'drawer') {
          document.documentElement.dispatchEvent(new CustomEvent('cart-notification:show', { bubbles: true, cancelable: true, detail: { status: 'success' } }));
        }
      } catch (error) {
        this._showNotice(error.message || config.strings.cartError);
      } finally {
        button.removeAttribute('disabled');
        button.removeAttribute('aria-busy');
      }
    }

    _openQuickBuy(item) {
      const isPhone = window.matchMedia('(max-width: 740px)').matches;
      const quickBuy = document.getElementById(isPhone ? 'wishlist-quick-buy-popover' : 'wishlist-quick-buy-drawer');

      if (!quickBuy) {
        window.location.href = item.url;
        return;
      }

      this._setOpen(false);
      quickBuy.innerHTML = '';
      quickBuy.setAttribute('href', item.url);
      quickBuy.open = true;
    }

    _showNotice(message) {
      clearTimeout(this.noticeTimeout);
      this.noticeElement.textContent = message;
      this.noticeElement.hidden = false;
      this.noticeTimeout = setTimeout(() => this.noticeElement.hidden = true, 5000);

      if (!this.open) {
        this._setOpen(true);
      }
    }

    _render() {
      const items = store.items;

      this.classList.toggle('has-items', items.length > 0);
      this.countElement.textContent = items.length;
      this.countElement.hidden = items.length === 0;
      this.countLabelElement.textContent = items.length > 0 ? `(${items.length})` : '';
      this.emptyElement.hidden = items.length > 0;
      this.listElement.hidden = items.length === 0;
      this.listElement.replaceChildren(...items.map((item) => this._renderItem(item)));
    }

    _renderItem(item) {
      const strings = config.strings;
      const element = document.createElement('li');
      element.className = 'wishlist-item';

      const imageLink = document.createElement('a');
      imageLink.className = 'wishlist-item__image-wrapper';
      imageLink.href = item.url;
      imageLink.tabIndex = -1;

      if (item.image) {
        const image = document.createElement('img');
        image.className = 'wishlist-item__image';
        image.src = item.image;
        image.alt = item.title;
        image.loading = 'lazy';
        image.width = 80;
        imageLink.append(image);
      }

      const info = document.createElement('div');
      info.className = 'wishlist-item__info';

      const title = document.createElement('a');
      title.className = 'wishlist-item__title text--small';
      title.href = item.url;
      title.textContent = item.title;

      const price = document.createElement('span');
      price.className = 'wishlist-item__price text--small text--subdued';
      price.textContent = item.price;

      const cartButton = document.createElement('button');
      cartButton.type = 'button';
      cartButton.className = 'wishlist-item__cart button button--primary button--small';
      cartButton.setAttribute('data-action', 'add-to-cart');
      cartButton.setAttribute('data-product-id', item.id);

      if (!item.available) {
        cartButton.disabled = true;
        cartButton.textContent = strings.soldOut;
      } else {
        cartButton.textContent = item.singleVariant ? strings.addToCart : strings.chooseOptions;
      }

      info.append(title, price, cartButton);

      const removeButton = document.createElement('button');
      removeButton.type = 'button';
      removeButton.className = 'wishlist-item__remove tap-area';
      removeButton.setAttribute('data-action', 'remove');
      removeButton.setAttribute('data-product-id', item.id);
      removeButton.setAttribute('aria-label', strings.remove.replace('[title]', item.title));
      removeButton.innerHTML = this.launcher.querySelector('svg').outerHTML;

      element.append(imageLink, info, removeButton);
      return element;
    }
  }

  window.customElements.define('wishlist-button', WishlistButton);
  window.customElements.define('wishlist-drawer', WishlistDrawer);
})();
